(() => {
  'use strict';

  const initializedMenus = new WeakSet();
  const initializedParallax = new WeakSet();
  const initializedGalleries = new WeakSet();
  const initializedProducts = new WeakSet();

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const finePointer = window.matchMedia('(pointer: fine)');
  const desktopHeader = window.matchMedia('(min-width: 990px)');

  let menuPanelIndex = 0;
  let galleryIndex = 0;

  function elementsWithin(scope, selector) {
    const elements = [];

    if (scope instanceof Element && scope.matches(selector)) {
      elements.push(scope);
    }

    if (typeof scope.querySelectorAll === 'function') {
      elements.push(...scope.querySelectorAll(selector));
    }

    return elements;
  }

  function announce(message) {
    const liveRegion = document.querySelector('.ac-live');

    if (!liveRegion || !message) return;

    liveRegion.textContent = '';
    window.requestAnimationFrame(() => {
      liveRegion.textContent = message;
    });
  }

  function syncMenuState(details) {
    const summary = details.querySelector(':scope > summary');
    const panel = details.querySelector('.ac-mobile-menu__panel');

    if (!summary || !panel) return;

    summary.setAttribute('aria-expanded', String(details.open));
    summary.setAttribute('aria-label', details.open ? 'Close menu' : 'Open menu');
  }

  function closeOtherMenus(currentMenu) {
    document.querySelectorAll('.ac-mobile-menu[open]').forEach((menu) => {
      if (menu !== currentMenu) menu.removeAttribute('open');
    });
  }

  function initializeMobileMenus(scope) {
    elementsWithin(scope, '.ac-mobile-menu').forEach((details) => {
      if (initializedMenus.has(details)) return;

      const summary = details.querySelector(':scope > summary');
      const panel = details.querySelector('.ac-mobile-menu__panel');

      if (!summary || !panel) return;

      if (!panel.id) {
        menuPanelIndex += 1;
        panel.id = `ac-mobile-menu-panel-${menuPanelIndex}`;
      }

      summary.setAttribute('aria-controls', panel.id);
      syncMenuState(details);

      details.addEventListener('toggle', () => {
        if (details.open) closeOtherMenus(details);
        syncMenuState(details);
      });

      panel.addEventListener('click', (event) => {
        if (event.target.closest('a')) details.removeAttribute('open');
      });

      initializedMenus.add(details);
    });
  }

  function closeMenusForOutsideClick(event) {
    document.querySelectorAll('.ac-mobile-menu[open]').forEach((details) => {
      if (!details.contains(event.target)) details.removeAttribute('open');
    });
  }

  function closeMenuWithEscape(event) {
    if (event.key !== 'Escape') return;

    const openMenus = [...document.querySelectorAll('.ac-mobile-menu[open]')];
    if (!openMenus.length) return;

    const activeMenu = openMenus.find((menu) => menu.contains(document.activeElement)) || openMenus.at(-1);
    const summary = activeMenu.querySelector(':scope > summary');

    activeMenu.removeAttribute('open');
    summary?.focus();
  }

  function closeMenusAtDesktopWidth(event) {
    if (!event.matches) return;
    document.querySelectorAll('.ac-mobile-menu[open]').forEach((details) => {
      details.removeAttribute('open');
    });
  }

  function resetParallax(root) {
    root.style.setProperty('--ac-pointer-x', '0');
    root.style.setProperty('--ac-pointer-y', '0');
    root.style.setProperty('--ac-parallax-x', '0px');
    root.style.setProperty('--ac-parallax-y', '0px');
    root.style.setProperty('--x', '0');
    root.style.setProperty('--y', '0');
  }

  function initializeParallax(scope) {
    elementsWithin(scope, '[data-ac-parallax]').forEach((root) => {
      if (initializedParallax.has(root)) return;

      let frame = 0;
      let nextX = 0;
      let nextY = 0;

      const render = () => {
        frame = 0;

        if (reducedMotion.matches || !finePointer.matches) {
          resetParallax(root);
          return;
        }

        root.style.setProperty('--ac-pointer-x', nextX.toFixed(4));
        root.style.setProperty('--ac-pointer-y', nextY.toFixed(4));
        root.style.setProperty('--ac-parallax-x', `${(nextX * 24).toFixed(2)}px`);
        root.style.setProperty('--ac-parallax-y', `${(nextY * 18).toFixed(2)}px`);
        root.style.setProperty('--x', nextX.toFixed(4));
        root.style.setProperty('--y', nextY.toFixed(4));
      };

      const requestRender = () => {
        if (!frame) frame = window.requestAnimationFrame(render);
      };

      root.addEventListener('pointermove', (event) => {
        if (reducedMotion.matches || !finePointer.matches) return;

        const bounds = root.getBoundingClientRect();
        if (!bounds.width || !bounds.height) return;

        nextX = Math.max(-0.5, Math.min(0.5, (event.clientX - bounds.left) / bounds.width - 0.5));
        nextY = Math.max(-0.5, Math.min(0.5, (event.clientY - bounds.top) / bounds.height - 0.5));
        requestRender();
      }, { passive: true });

      root.addEventListener('pointerleave', () => {
        nextX = 0;
        nextY = 0;
        requestRender();
      }, { passive: true });

      resetParallax(root);
      initializedParallax.add(root);
    });
  }

  function resetAllParallax() {
    document.querySelectorAll('[data-ac-parallax]').forEach(resetParallax);
  }

  function pauseHiddenMedia(media) {
    media.querySelectorAll('video').forEach((video) => video.pause());
  }

  function initializeProductGalleries(scope) {
    elementsWithin(scope, '[data-product-gallery]').forEach((gallery) => {
      if (initializedGalleries.has(gallery)) return;

      const mediaItems = [...gallery.querySelectorAll('[data-media-id]')];
      const thumbnails = [...gallery.querySelectorAll('[data-media-target]')];

      if (!mediaItems.length || !thumbnails.length) {
        initializedGalleries.add(gallery);
        return;
      }

      galleryIndex += 1;
      const currentGalleryIndex = galleryIndex;
      const thumbnailList = gallery.querySelector('.ac-product-thumbs');

      thumbnailList?.setAttribute('role', 'tablist');

      const activateMedia = (mediaId, options = {}) => {
        const selectedIndex = thumbnails.findIndex((thumbnail) => thumbnail.dataset.mediaTarget === mediaId);
        if (selectedIndex < 0) return;

        thumbnails.forEach((thumbnail, index) => {
          const selected = index === selectedIndex;
          thumbnail.classList.toggle('active', selected);
          thumbnail.setAttribute('aria-selected', String(selected));
          thumbnail.tabIndex = selected ? 0 : -1;

          if (selected && options.focus) thumbnail.focus();
        });

        mediaItems.forEach((media) => {
          const selected = media.dataset.mediaId === mediaId;
          media.classList.toggle('active', selected);
          media.hidden = !selected;

          if (!selected) pauseHiddenMedia(media);
        });

        if (options.announce) {
          announce(`Product media ${selectedIndex + 1} of ${thumbnails.length}.`);
        }
      };

      mediaItems.forEach((media, index) => {
        if (!media.id) media.id = `ac-product-media-${currentGalleryIndex}-${index + 1}`;
        media.setAttribute('role', 'tabpanel');
        media.tabIndex = 0;
      });

      thumbnails.forEach((thumbnail, index) => {
        const mediaId = thumbnail.dataset.mediaTarget;
        const media = mediaItems.find((item) => item.dataset.mediaId === mediaId);

        if (!thumbnail.id) thumbnail.id = `ac-product-media-tab-${currentGalleryIndex}-${index + 1}`;
        thumbnail.setAttribute('role', 'tab');

        if (media) {
          thumbnail.setAttribute('aria-controls', media.id);
          media.setAttribute('aria-labelledby', thumbnail.id);
        }

        thumbnail.addEventListener('click', () => {
          activateMedia(mediaId, { announce: true });
        });

        thumbnail.addEventListener('keydown', (event) => {
          let nextIndex = null;

          if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
            nextIndex = (index + 1) % thumbnails.length;
          } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
            nextIndex = (index - 1 + thumbnails.length) % thumbnails.length;
          } else if (event.key === 'Home') {
            nextIndex = 0;
          } else if (event.key === 'End') {
            nextIndex = thumbnails.length - 1;
          }

          if (nextIndex === null) return;

          event.preventDefault();
          activateMedia(thumbnails[nextIndex].dataset.mediaTarget, { focus: true, announce: true });
        });
      });

      const initiallyActive = thumbnails.find((thumbnail) => thumbnail.classList.contains('active')) || thumbnails[0];
      activateMedia(initiallyActive.dataset.mediaTarget);
      initializedGalleries.add(gallery);
    });
  }

  function updatePrice(priceElement, price, comparePrice) {
    if (!priceElement || !price) return;

    priceElement.textContent = price;

    const compareDigits = comparePrice.replace(/[^0-9]/g, '');
    const hasComparePrice = compareDigits && Number(compareDigits) > 0;

    if (hasComparePrice && comparePrice !== price) {
      priceElement.append(document.createTextNode(' '));
      const compare = document.createElement('s');
      compare.textContent = comparePrice;
      priceElement.append(compare);
    }
  }

  function updateVariantUrl(variantId) {
    if (!variantId || !window.history?.replaceState) return;

    const url = new URL(window.location.href);
    url.searchParams.set('variant', variantId);
    window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
  }

  function initializeProductForms(scope) {
    elementsWithin(scope, '[data-product-root]').forEach((root) => {
      if (initializedProducts.has(root)) return;

      const form = root.querySelector('[data-product-form]');
      const select = form?.querySelector('[data-variant-select]');
      const priceElement = root.querySelector('[data-product-price]');
      const addButton = form?.querySelector('[data-add-to-cart]');
      const addLabel = addButton?.querySelector('[data-add-label]');

      if (!form || !select) {
        initializedProducts.add(root);
        return;
      }

      const applySelectedVariant = ({ shouldAnnounce = false, shouldUpdateUrl = false } = {}) => {
        const option = select.options[select.selectedIndex];
        if (!option) return;

        const variantId = option.value;
        const price = option.dataset.price || '';
        const comparePrice = option.dataset.comparePrice || '';
        const available = String(option.dataset.available).toLowerCase() === 'true';

        updatePrice(priceElement, price, comparePrice);

        if (addButton) {
          addButton.disabled = !available;
          if (available) {
            addButton.removeAttribute('aria-disabled');
          } else {
            addButton.setAttribute('aria-disabled', 'true');
          }
        }

        if (addLabel) addLabel.textContent = available ? 'Add to bag' : 'Sold out';
        if (shouldUpdateUrl) updateVariantUrl(variantId);

        root.dispatchEvent(new CustomEvent('ac:variant-change', {
          bubbles: true,
          detail: { variantId, available }
        }));

        if (shouldAnnounce) {
          const selectedLabel = option.textContent.replace(/\s+/g, ' ').trim();
          announce(`${selectedLabel}. ${available ? 'Available.' : 'Sold out.'}`);
        }
      };

      select.addEventListener('change', () => {
        applySelectedVariant({ shouldAnnounce: true, shouldUpdateUrl: true });
      });

      applySelectedVariant();
      initializedProducts.add(root);
    });
  }


  function initializeVariantPills(scope) {
    elementsWithin(scope, '[data-product-root]').forEach((root) => {
      const pills = root.querySelectorAll('[data-variant-id]');
      const select = root.querySelector('[data-variant-select]');
      if (!pills.length || !select || root.dataset.pillsReady) return;
      root.dataset.pillsReady = 'true';

      const syncPills = () => {
        pills.forEach((pill) => {
          const active = pill.dataset.variantId === select.value;
          pill.classList.toggle('is-selected', active);
          pill.setAttribute('aria-checked', String(active));
        });
      };

      pills.forEach((pill) => {
        pill.addEventListener('click', () => {
          if (pill.disabled) return;
          select.value = pill.dataset.variantId;
          select.dispatchEvent(new Event('change', { bubbles: true }));
          syncPills();
        });
      });

      select.addEventListener('change', syncPills);
      syncPills();
    });
  }

  function initializeQtySteppers(scope) {
    elementsWithin(scope, '[data-qty-stepper]').forEach((stepper) => {
      if (stepper.dataset.ready) return;
      stepper.dataset.ready = 'true';
      const input = stepper.querySelector('input[name="quantity"]');
      const minus = stepper.querySelector('[data-qty-minus]');
      const plus = stepper.querySelector('[data-qty-plus]');
      if (!input || !minus || !plus) return;
      const min = parseInt(input.min || '1', 10);
      const max = parseInt(input.max || '0', 10);
      minus.addEventListener('click', () => {
        input.value = Math.max(min, (parseInt(input.value, 10) || min) - 1);
      });
      plus.addEventListener('click', () => {
        const next = (parseInt(input.value, 10) || min) + 1;
        input.value = max > 0 ? Math.min(max, next) : next;
      });
    });
  }

  function initializeStickyBar(scope) {
    elementsWithin(scope, '[data-product-root]').forEach((root) => {
      const bar = root.querySelector('[data-sticky-bar]');
      const form = root.querySelector('[data-product-form]');
      if (!bar || !form || bar.dataset.ready) return;
      bar.dataset.ready = 'true';
      const sentinel = form.querySelector('[data-add-to-cart]');
      const stickyPrice = bar.querySelector('[data-sticky-price]');
      const stickyBtn = bar.querySelector('[data-add-to-cart]');
      const stickyLabel = bar.querySelector('[data-add-label]');
      if (!sentinel) return;

      const onScroll = () => {
        const rect = sentinel.getBoundingClientRect();
        bar.classList.toggle('is-visible', rect.bottom < 0);
      };
      window.addEventListener('scroll', onScroll, { passive: true });
      onScroll();

      root.addEventListener('ac:variant-change', (event) => {
        const select = form.querySelector('[data-variant-select]');
        const option = select && select.options[select.selectedIndex];
        if (option && stickyPrice) stickyPrice.textContent = option.dataset.price || '';
        if (stickyBtn) {
          stickyBtn.disabled = !event.detail.available;
          if (stickyLabel) stickyLabel.textContent = event.detail.available ? 'Add to bag' : 'Sold out';
        }
      });
    });
  }

  function initialize(scope = document) {
    initializeMobileMenus(scope);
    initializeParallax(scope);
    initializeProductGalleries(scope);
    initializeProductForms(scope);
    initializeVariantPills(scope);
    initializeQtySteppers(scope);
    initializeStickyBar(scope);
  }

  document.addEventListener('click', closeMenusForOutsideClick);
  document.addEventListener('keydown', closeMenuWithEscape);

  if (typeof desktopHeader.addEventListener === 'function') {
    desktopHeader.addEventListener('change', closeMenusAtDesktopWidth);
    reducedMotion.addEventListener('change', resetAllParallax);
    finePointer.addEventListener('change', resetAllParallax);
  } else {
    desktopHeader.addListener(closeMenusAtDesktopWidth);
    reducedMotion.addListener(resetAllParallax);
    finePointer.addListener(resetAllParallax);
  }

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  } else {
    initialize();
  }
})();
